/**
 * Extract a user-friendly Hungarian error message from an axios error.
 * Tries to use the backend's message first, then falls back to a status-code-based message.
 */
export function getErrorMessage(err: any, fallback = 'Hiba történt a művelet során'): string {
  // Network error (no response from server)
  if (!err?.response) {
    return 'Hálózati hiba - nem sikerült elérni a szervert';
  }

  const status = err.response.status;
  const data = err.response.data;

  // Try to extract a meaningful message from the response body
  const backendMessage =
    data?.message
    || data?.error
    || data?.errorMessage
    || (typeof data === 'string' ? data : null);

  // Validation errors (RFC 7807 ProblemDetails with `errors` object)
  if (data?.errors && typeof data.errors === 'object') {
    const validationMessages = Object.values(data.errors)
      .flat()
      .filter((m): m is string => typeof m === 'string');
    if (validationMessages.length > 0) {
      return validationMessages.join(' ');
    }
  }

  // If the backend gave us a real message, use it
  if (backendMessage) return backendMessage;

  // Fall back to status-code-based Hungarian messages
  switch (status) {
    case 400: return 'Érvénytelen kérés. Ellenőrizd a megadott adatokat.';
    case 401: return 'Nincs bejelentkezve, vagy lejárt a munkamenet.';
    case 403: return 'Nincs jogosultságod ehhez a művelethez.';
    case 404: return 'A keresett elem nem található, vagy nincs jogosultságod hozzá.';
    case 409: return 'Ütközés - az adat már létezik vagy konfliktus van.';
    case 413: return 'A fájl túl nagy.';
    case 422: return 'Érvényesítési hiba - ellenőrizd az adatokat.';
    case 429: return 'Túl sok kérés rövid időn belül. Próbáld újra később.';
    case 500: return 'Szerverhiba - próbáld újra később.';
    case 502:
    case 503:
    case 504: return 'A szerver jelenleg nem elérhető. Próbáld újra később.';
    default: return fallback;
  }
}
